import {useContext, useState, useEffect} from "react";
import axios from "axios";
import styles from "./Card.module.css";
import {MobileWrapper} from "../../common/MobileWrapper";
import {Header} from "../../common/Header";
import {SupportEmail} from "../../common/SupportFooter";
import {RoutingContext} from "../../../context/Router";
import {AmountSection} from "../../common/AmountSection";
import {Details} from "./screens/Details";
import {BackToMethods} from "../../common/BackToMethods";
import {WalletPayProps} from "../Wallet";
import {listenToCardTransStatus, sendMessage} from "../../../context/utils";
import {StatusMessage} from "../../StatusMessage";
import {Pin} from "./screens/Pin";
import {Otp} from "./screens/Otp";
import {StatusContext} from "../../../context/Status";
import {ErrorAlertContext} from "../../../context/ErrorAlert";
import classNames from "classnames/bind";
import {generateApiUrl} from "./utilities";
import Loader from "../../common/Loader";
import NoAuth from "./screens/NoAuth";

const cx = classNames.bind(styles);

export const Card = () => {
    const {email, currency, amount, merchantKey, payData} =
        useContext(RoutingContext);

    const {setPageTransactionStatus} = useContext(StatusContext);
    const {onShowAlert} = useContext(ErrorAlertContext);

    const [tabDisplayed, setTabDisplayed] = useState("card");

    const [transactionRef, setTransactionRef] = useState("");
    const [flwRef, setFlwRef] = useState("");
    const [formData, setFormData] = useState({
        country: '',
        city: '',
        zipcode: '',
        state: '',
        address: '',
        mode: '',
        tx_ref: ''
    });
    const [otpMessage, setOtpMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [transferLoading, setTransferLoading] = useState(false)
    const url = generateApiUrl(currency);
    const authorization = generateApiUrl(currency, "pin");
    const otpAuthorization = generateApiUrl(currency, "otp");


    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const {name, value} = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };


    const onPay = async (data?: WalletPayProps) => {
        try {
            setIsLoading(true);
            const dateSplit = data?.exp.split("/");
            const cardNumber = data?.card.replace(/\s/g, "");
            const expMonth = dateSplit[0];
            const expYear = dateSplit[1];
            const commonPayload = {
                ...payData.kit,
                card_number: cardNumber,
                card_holder_name: data?.cardHolder,
                cvv: data?.cvv,
                expiry_month: expMonth,
                expiry_year: expYear,
                sourceCurrency: payData.sourceCurrency,
                paymentType: payData.kit?.paymentType ?? 'html-checkout',
                productType: payData.kit.productType ?? "COLLECTION",
                paylinkToken: payData.kit.paylinkToken,

            };

            const response = await axios({
                method: "post",
                url: url,
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    "x-auth-token": merchantKey,
                    ...(process.env.REACT_APP_DASHBOARD_ACCESS_TOKEN && {
                        "x-fe-token": process.env.REACT_APP_DASHBOARD_ACCESS_TOKEN
                    })
                },
                data: commonPayload,
            });


            const responseData = response.data;

            if (responseData?.status === 'error') {
                handleErrorResponse(responseData);
            } else {
                const authorization = responseData?.data?.meta?.authorization;
                const referrerCard = responseData?.data?.status;
                const flwRefFromResponse = responseData?.data?.flw_ref;

                if (authorization?.mode === 'avs_noauth') {
                    setPageTransactionStatus("avs_noauth");
                    setTabDisplayed("avs_noauth");
                    setFormData((prevData) => ({
                        ...prevData,
                        mode: authorization.mode,
                        tx_ref: responseData?.data?.tx_ref || payData.kit.tx_ref,
                    }));
                } else if (authorization?.mode === 'redirect' && authorization.redirect) {
                    setFlwRef(flwRefFromResponse);
                    const newTab = window.open(authorization.redirect, "_blank");
                    if (newTab) {
                        newTab.opener = null;
                        sendMessage({card: "redirect"});
                        try {
                            listenToCardTransStatus(flwRefFromResponse, setTabDisplayed, setPageTransactionStatus);
                        } catch (error) {
                            handleRedirectError();
                        }
                    } else {
                        window.location.href = authorization.redirect;
                    }
                } else if (referrerCard === "send_pin") {
                    setPageTransactionStatus("pin");
                    setTabDisplayed("pin");
                } else if (referrerCard === "send_otp") {
                    setPageTransactionStatus("otp");
                    setTabDisplayed("otp");
                } else if (referrerCard === "open_url") {
                    const redirectUrl = responseData?.data?.url;
                    if (redirectUrl) {
                        window.location.replace(redirectUrl);
                    } else {
                        handleErrorResponse({message: "Payment redirect URL was not found."});
                    }
                } else if (responseData?.chargeResponseCode === "00" || responseData?.data?.status === 'successful') {
                    setTabDisplayed("success");
                    sendMessage(responseData);
                } else {
                    handleNonErrorStatus(responseData, commonPayload);
                }
            }

            setIsLoading(false);
        } catch (error) {
            handlePaymentFailure(error);
        }
    };

    const handleNonErrorStatus = (responseData: any, commonPayload: any) => {
        const paymentMode = responseData?.data?.meta?.authorization?.mode;
        const transactionRef = responseData.tx_ref;
        if (paymentMode === "pin") {
            setTabDisplayed("pin");
            setPageTransactionStatus("pin");
            setTransactionRef(transactionRef);
        }
    };


    const handleRedirectError = () => {
        setTabDisplayed("error");
        setPageTransactionStatus("error");
        setTimeout(() => {
            sendMessage({status: "ERROR", tnxRef: payData.kit.tx_ref});
        }, 5000);
    };

    const handleErrorResponse = (responseData: any) => {
        onShowAlert(responseData, responseData?.message);
        setTabDisplayed("error");
        setPageTransactionStatus("error");
        setTimeout(() => {
            sendMessage({status: "ERROR", tnxRef: payData.kit.tx_ref});
        }, 10000);
    };

    const handlePaymentFailure = (error: any) => {
        const errorMessage = error?.response?.data?.message || `Payment failed. Please try again.`;
        onShowAlert(error, errorMessage);
        setTabDisplayed("error");
        setPageTransactionStatus("error");
        setIsLoading(false);
        setTimeout(() => {
            sendMessage({status: "ERROR", tnxRef: payData.kit.tx_ref});
        }, 5000);
    };


    const onPinSubmit = async (pinValue: string) => {
        localStorage.setItem("pinVal", pinValue);
        const payload = {
            mode: "pin",
            pin: pinValue,
            tx_ref: payData.kit.tx_ref,
        };

        setTransferLoading(true);
        setIsLoading(true);

        try {
            const response = await axios.post(authorization, payload, {
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    "x-auth-token": merchantKey,
                    ...(process.env.REACT_APP_DASHBOARD_ACCESS_TOKEN && {
                        "x-fe-token": process.env.REACT_APP_DASHBOARD_ACCESS_TOKEN
                    })
                }
            });

            const responseData = response.data;

            switch (responseData.status) {
                case 'successful':
                    setTabDisplayed("success");
                    setPageTransactionStatus("success");
                    setTimeout(() => {
                        sendMessage({status: "successful", tnxRef: payData.kit.tx_ref});
                    }, 1000);
                    break;

                case 'pending':
                    setTabDisplayed('otp');
                    setFlwRef(responseData.flw_ref);
                    setPageTransactionStatus("otp");
                    break;

                default:
                    if (responseData?.data?.status === "send_otp") {
                        setTabDisplayed("otp");
                        setPageTransactionStatus("otp");
                    } else if (responseData.message?.includes("OTP")) {
                        setTabDisplayed("otp");
                        setPageTransactionStatus("otp");
                        setOtpMessage(responseData.message);
                    } else {
                        onShowAlert(responseData, responseData.message || "An unknown error occurred");
                    }
                    break;
            }
        } catch (error: any) {
            const errorMessage = error?.response?.data?.message || "Something went wrong, please try again.";
            onShowAlert("", errorMessage);

            setPageTransactionStatus("error");
            setTabDisplayed("error");
        } finally {
            setTransferLoading(false);
            setIsLoading(false);
        }
    };


    const onOtpSubmit = async (otpValue: string) => {

        const payload = {
            otp: otpValue,
            flw_ref: flwRef,
            type: "card",
            tx_ref: payData.kit.tx_ref
        };

        setTransferLoading(true)

        try {
            const response = await axios({
                method: "post",
                url: otpAuthorization,
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    "x-auth-token": merchantKey,
                    ...(process.env.REACT_APP_DASHBOARD_ACCESS_TOKEN && {
                        "x-fe-token": process.env.REACT_APP_DASHBOARD_ACCESS_TOKEN
                    })
                },
                data: payload,
            });

            setTransferLoading(false)
            const responseData = response.data;

            if (responseData?.data?.meta?.authorization?.mode === 'redirect') {
                window.location.href = responseData.data.meta.authorization.redirect;
                return;
            }

            console.log(responseData, '--api-response')
            if (responseData.status === "successful") {
                setTabDisplayed("success");
                setPageTransactionStatus("success");
                setTimeout(() => {
                    sendMessage({status: "successful", tnxRef: payData.kit.tx_ref});
                }, 1000);
            }


            if (responseData.status === "failed" && responseData.processor_response === "invalid token supplied") {
                // Handle invalid token case
            }
            if (responseData.status === "failed" && responseData.processor_response === "Incorrect PIN") {
                setTabDisplayed("error");
                setPageTransactionStatus("error");
            }
            if (responseData.status === "error") {
                setTabDisplayed("error");
                setPageTransactionStatus("error");
            }
            if (responseData.data.status === "successful") {
                setTabDisplayed("success");
                setPageTransactionStatus("success");
            } else if (responseData.data.status === "successful") {
                setTabDisplayed("success");
                setPageTransactionStatus("success");
            } else {
                setTabDisplayed("error");
                setPageTransactionStatus("error");
            }

            setIsLoading(false);
            setTimeout(() => {
                sendMessage({status: responseData, tnxRef: payData.kit.tx_ref});
            }, 5000);
        } catch (e: any) {
            onShowAlert("", e.response.data.message)
            setTransferLoading(false)
            setTabDisplayed("error");
            setIsLoading(false);
            setPageTransactionStatus("error");
            setTimeout(() => {
                sendMessage({status: "ERROR", tnxRef: payData.kit.tx_ref});
            }, 5000);
        }
    };


    const NoAuthPayment = async () => {
        const updatedFormData = {
            ...formData,
        };
        setTransferLoading(true);
        try {
            const response = await axios({
                method: "post",
                url: `${process.env.REACT_APP_API_URL}pay/${currency}/charge`,
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    "x-auth-token": merchantKey,
                    ...(process.env.REACT_APP_DASHBOARD_ACCESS_TOKEN && {
                        "x-fe-token": process.env.REACT_APP_DASHBOARD_ACCESS_TOKEN
                    })
                },
                data: updatedFormData,
            });
            const responseData = response.data;
            setFlwRef(responseData.flw_ref);

            if (responseData.mode === "redirect" && responseData.redirect) {
                window.location.replace(responseData.redirect);
                return;
            }

            setTransferLoading(false);

            if (responseData.status === "successful" || responseData?.data?.status === "successful") {
                setTabDisplayed("success");
                setPageTransactionStatus("success");
                setTimeout(() => {
                    sendMessage({status: "successful", tnxRef: payData.kit.tx_ref});
                }, 1000);
            } else if (responseData.status === "pending") {
                setTabDisplayed("otp");
            } else {
                handleErrorResponse(responseData.message ? responseData : {message: "Payment failed or an unknown error occurred."});
            }
            setIsLoading(false);
        } catch (e: any) {
            onShowAlert("", e.response.data.message);
            setTransferLoading(false);
            setTabDisplayed("error");
            setIsLoading(false);
            setPageTransactionStatus("error");
            setTimeout(() => {
                sendMessage({status: "ERROR", tnxRef: payData.kit.tx_ref});
            }, 5000);
        }
    };


    if (tabDisplayed === "error" || tabDisplayed === "success") {
        return <StatusMessage type={tabDisplayed}/>;
    }


    return (
        <MobileWrapper>
            <Loader isLoading={transferLoading}/>

            <div
                className={cx({
                    container: true,
                    isPin: ["otp", "pin"].includes(tabDisplayed),
                })}
            >
                <div className={styles.main}>
                    <Header email={email}/>
                    <AmountSection amount={amount} currency={payData?.details?.currency}/>
                    {tabDisplayed === "card" && (
                        <>
                            <Details onPay={onPay} isLoading={isLoading}/>
                            <BackToMethods/>
                        </>
                    )}
                    {tabDisplayed === "pin" && (
                        <Pin
                            onSubmit={onPinSubmit}
                            // pinValue={localStorage.getItem("pinVal")}
                        />
                    )}
                    {tabDisplayed === "otp" && (
                        <Otp onSubmit={onOtpSubmit} message={otpMessage}/>
                    )}
                </div>
                <div>
                    {tabDisplayed === "avs_noauth" && (
                        <NoAuth formData={formData} handleChange={handleChange} handleFormSubmit={NoAuthPayment}
                                loading={transferLoading}/>
                    )}
                </div>
                <SupportEmail/>
            </div>
        </MobileWrapper>
    );
};
